import { loadScript } from '@vfde-sails/utils';
import {
    SCRIPT_MTCAPTCHA,
    SCRIPT_MTCAPTCHA2,
} from '../constants';
import { CaptchaEvents } from '../enum';

/**
 * Creates and dispatches a custom captcha event to handle different captcha scenarios
 * @param {CaptchaEvents} type - type of the event
 * @param {any=} payload - payload that you want to add to the custom event
 */
export const dispatchCaptchaEvent = (type: CaptchaEvents, payload?: any) => {
    const event = new CustomEvent(type, { detail: payload });

    document.dispatchEvent(event);
};

/**
 * Subscribes to 'mtVerified' and 'mtRejected' CustomEvents. These events determine
 * if a user successfully completed the captcha or that the flow was cancelled
 * On 'mtVerified' resolve the returned Promise
 * On 'mtRejected' reject the returned Promise
 * @returns {Promise<void>} - resolved Promise when captcha is verified
 */
export const listenForCaptchaVerify = (): Promise<void> => {
    return new Promise((resolve, reject) => {
        const verifiedEventHandler = () => {
            document.removeEventListener(CaptchaEvents.VERIFIED, verifiedEventHandler);

            resolve();
        };

        const rejectedEventHandler = () => {
            document.removeEventListener(CaptchaEvents.REJECTED, rejectedEventHandler);

            reject(new Error('Captcha validation interrupted'));
        };

        document.addEventListener(CaptchaEvents.VERIFIED, verifiedEventHandler);
        document.addEventListener(CaptchaEvents.REJECTED, rejectedEventHandler);
    });
};

/**
 * Resets the captcha UI and subscribes to 'mtRendered' and 'mtVerified' CustomEvents.
 * When any of the CustomEvent fires (meaning the captcha is verified)
 * it returns a resolved Promise and removes the subscribed event handlers.
 * @throws {Error} - mtCaptcha object is not declared on the window object
 * @returns {Promise<void>} - resolve if captcha is verified, reject if captcha doesnt exist
 */
export const resetCaptcha = (): Promise<void> => {
    return new Promise((resolve, reject) => {
        if (!window.mtcaptcha) {
            reject(new Error('mtCaptcha not found'));

            return;
        }

        const resolvedEventHandler = () => {
            document.removeEventListener(CaptchaEvents.RENDERED, resolvedEventHandler);
            document.removeEventListener(CaptchaEvents.VERIFIED, resolvedEventHandler);

            resolve();
        };

        const rejectedEventHandler = () => {
            document.removeEventListener(CaptchaEvents.REJECTED, rejectedEventHandler);

            reject(new Error('Captcha validation interrupted'));
        };

        document.addEventListener(CaptchaEvents.RENDERED, resolvedEventHandler);
        document.addEventListener(CaptchaEvents.VERIFIED, resolvedEventHandler);
        document.addEventListener(CaptchaEvents.REJECTED, rejectedEventHandler);

        window.mtcaptcha.resetUI();
    });
};

/**
 * Loads the mtCaptcha's scripts into the <head> tag.
 * Sets the captcha's config on the window object
 */
export const mountCaptcha = () => {
    loadScript(SCRIPT_MTCAPTCHA, document.head);
    loadScript(SCRIPT_MTCAPTCHA2, document.head);

    window.mtcaptchaConfig = {
        sitekey: 'MTPublic-s9jH2tStR',
        theme: 'highcontrast',
        action: 'api-call',
        challangeType: 'imageonly',
        autoFormValidate: false,
        'verified-callback': () => dispatchCaptchaEvent(CaptchaEvents.VERIFIED),
        'rendered-callback': () => dispatchCaptchaEvent(CaptchaEvents.RENDERED),
        customStyle: {
            showCard: true,
            cardShadow: false,
            cardShadowColor: '#ffffff',
            cardBorder: '#eeeeee',
            inputTextColor: '#333333',
            msgTextColor: '#cccccc',
            invalidMsgTextColor: '#e60000',
            inputBorderColor: {
                byDefault: '#cccccc',
                hover: '#afafaf',
                active: '#333333',
            },
            buttonIconColor: {
                verify: '#afafaf',
                success: '#428600',
                fail: '#e60000',
                audio: '#333333',
                audiofocus: '#333333',
                focus: '#333333',
            },
        },
        lang: 'de',
        customLangText: {
            de: window.additionalPageOptions?.captcha,
        },
    };
};

/**
 * Get verified captcha token.
 * @returns {string} token or empty string if captcha is not valid or doesnt exist
 */
export const getCaptchaToken = () => window.mtcaptcha?.getVerifiedToken() ?? '';
